import React from 'react';

const longDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const daysAgoText = (days) => {
    if (days === null || days === undefined) return '';
    if (days === 0) return 'today';
    return days === 1 ? '1 day ago' : `${days} days ago`;
};

const STATUS_STYLES = {
    Accepted: 'bg-emerald-900/30 text-emerald-300 border-emerald-700/40',
    Started: 'bg-amber-900/30 text-amber-300 border-amber-700/40',
    Open: 'bg-blue-900/30 text-blue-300 border-blue-700/40',
    Expired: 'bg-gray-800 text-gray-300 border-gray-600',
    Cancelled: 'bg-red-900/30 text-red-300 border-red-700/40'
};

const STATUS_LABELS = {
    Accepted: 'Accepted',
    Started: 'Started, not finished',
    Open: 'Open',
    Expired: 'Expired',
    Cancelled: 'Cancelled'
};

export const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[status] || STATUS_STYLES.Expired}`}>
        {STATUS_LABELS[status] || status}
    </span>
);

// Members who began setting up a Direct Debit and never finished, with the two ways to fix it.
export const StalledSignUpsView = ({ report, includeNeverStarted, onToggleNeverStarted, onSendLink, onLinkGoCardless }) => {
    const members = report?.members || [];

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <p className="text-sm text-gray-300 font-body" data-testid="stalled-summary">
                    {members.length === 0
                        ? 'No stalled sign ups. Everyone who started has finished.'
                        : `${members.length} member${members.length === 1 ? '' : 's'} ${includeNeverStarted ? 'have an account and no subscription' : 'started setting up a Direct Debit and never finished'}.`}
                </p>
                <label className="flex items-center gap-2 text-sm text-emerald-300/80 font-body cursor-pointer">
                    <input type="checkbox" checked={!!includeNeverStarted} onChange={(e) => onToggleNeverStarted(e.target.checked)} />
                    Also show members who never started (includes pay as you go members)
                </label>
            </div>

            {members.length > 0 && (
                <div className="card-luxury overflow-x-auto">
                    <table className="min-w-full divide-y divide-emerald-600/10">
                        <thead className="bg-luxury-charcoal/50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Member</th>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Plan or invitation</th>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Last tried</th>
                                <th className="px-4 py-3 text-center text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Tries</th>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Fix it</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-emerald-600/10">
                            {members.map((m) => (
                                <tr key={m.userId} data-testid="stalled-row">
                                    <td className="px-4 py-3">
                                        <p className="text-sm font-display font-medium text-accent-platinum">{m.name}</p>
                                        <p className="text-xs text-emerald-600/70 font-body">{m.email}</p>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-300 font-body">
                                        {m.planName || 'Not known'}
                                        {m.fromInvitation && <span className="ml-2 text-xs text-emerald-400">invitation</span>}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-300 font-body">
                                        {m.lastAttemptOn ? (
                                            <>
                                                {longDate(m.lastAttemptOn)}
                                                <span className="block text-xs text-amber-300">{daysAgoText(m.daysStalled)}</span>
                                            </>
                                        ) : (
                                            <span className="text-xs text-gray-500">Never started</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center text-sm text-gray-300">{m.attempts}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => onSendLink(m)}
                                                className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-emerald-600/20 text-emerald-300 border border-emerald-600/30 hover:bg-emerald-600/30 transition-colors"
                                            >
                                                Send sign up link
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onLinkGoCardless(m)}
                                                className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-blue-900/20 text-blue-300 border border-blue-700/30 hover:bg-blue-900/40 transition-colors"
                                            >
                                                Link in GoCardless
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const Tile = ({ label, value, testId }) => (
    <div className="card-luxury p-4 text-center">
        <p className="text-2xl font-display font-bold text-accent-platinum" data-testid={testId}>{value}</p>
        <p className="text-xs text-emerald-600/70 font-body mt-1">{label}</p>
    </div>
);

// How each invitation turned out, plus a summary of them all.
export const InvitationResultsView = ({ report, days, onDaysChange }) => {
    const offers = report?.offers || [];

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-4">
                <p className="text-sm text-gray-300 font-body" data-testid="invitation-summary">
                    {report?.acceptedPercent === null || report?.acceptedPercent === undefined
                        ? 'No invitations have had their chance yet.'
                        : `${report.acceptedPercent}% of invitations that have had their chance were accepted.`}
                </p>
                <select
                    value={days}
                    onChange={(e) => onDaysChange(parseInt(e.target.value, 10))}
                    className="px-3 py-2 bg-luxury-obsidian border border-emerald-700/30 rounded-lg text-accent-platinum text-sm"
                    aria-label="Time period"
                >
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={180}>Last 6 months</option>
                    <option value={365}>Last year</option>
                </select>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                <Tile label="Sent" value={report?.total ?? 0} testId="tile-total" />
                <Tile label="Accepted" value={report?.accepted ?? 0} testId="tile-accepted" />
                <Tile label="Started, not finished" value={report?.started ?? 0} testId="tile-started" />
                <Tile label="Still open" value={report?.open ?? 0} testId="tile-open" />
                <Tile label="Expired" value={report?.expired ?? 0} testId="tile-expired" />
                <Tile label="Cancelled" value={report?.cancelled ?? 0} testId="tile-cancelled" />
            </div>

            {offers.length === 0 ? (
                <p className="text-sm text-gray-400 font-body">No invitations in this time period.</p>
            ) : (
                <div className="card-luxury overflow-x-auto">
                    <table className="min-w-full divide-y divide-emerald-600/10">
                        <thead className="bg-luxury-charcoal/50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Sent to</th>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Plan</th>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Result</th>
                                <th className="px-4 py-3 text-center text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Used</th>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Sent</th>
                                <th className="px-4 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Expires</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-emerald-600/10">
                            {offers.map((o) => (
                                <tr key={o.offerId} data-testid="invitation-row">
                                    <td className="px-4 py-3">
                                        <p className="text-sm text-accent-platinum font-body">{o.recipientName || o.recipientEmail || 'Anyone with the link'}</p>
                                        {o.recipientName && o.recipientEmail && <p className="text-xs text-emerald-600/70">{o.recipientEmail}</p>}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-300 font-body">
                                        {o.planName}
                                        {o.isCustom && <span className="ml-2 text-xs text-emerald-400">custom</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusBadge status={o.status} />
                                        {o.acceptedBy && <p className="text-xs text-emerald-600/70 mt-1">by {o.acceptedBy}</p>}
                                    </td>
                                    <td className="px-4 py-3 text-center text-sm text-gray-300">{o.uses} of {o.maxUses}</td>
                                    <td className="px-4 py-3 text-sm text-gray-300 font-body">{longDate(o.createdOn)}</td>
                                    <td className="px-4 py-3 text-sm text-gray-300 font-body">{longDate(o.expiresOn)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="mt-4 text-xs text-emerald-600/60 font-body">
                A link only counts as accepted once the person finishes setting up their Direct Debit. We cannot see who opened a link but did nothing.
                Older invitations for standard plans may show as Open or Expired even if they were used, because their use was not counted before now.
            </p>
        </div>
    );
};
